/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import cx from "classnames";
import mongoose from "mongoose";
import Rut from "rut.js";
import lumaUtils from "@lumarketo/luma-utils";

// Iconos Material-UI v4
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import DescriptionIcon from "@material-ui/icons/Description";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import FlashOnIcon from "@material-ui/icons/FlashOn";

// CSS Modules
import styles from "./fintocPaymentSettings.module.css";

// Acciones y Reducers
import { getAllPayments as getPayments, updatePayment } from "../../../../reducers/paymentSlice";
import { Notification } from "../../../../containers/notification";
import { selectUser } from "../../../../reducers/userSlice";
import { loadingOff, loadingOn, selectLoadingUI } from "../../../../reducers/uiSlice";
import { selectCompany, getCompany } from "../../../../reducers/companySlice";
import { selectChangesDeposit, setChanges } from "../../../../reducers/depositSlice";
import { selectSubsidiaries } from "../../../../reducers/subsidiarySlice";

// Utilidades y Servicios
import paymentsConstants from "../../../../constants/payments.constants";
import { useChangeRecord } from "../../../../utils/hooks/useChangeRecord";
import companyService from "../../../../utils/services/company";
import paymentsService from "../../../../utils/services/payments";
import dateUtils from "../../../../utils/date";
import config from "../../../../utils/config";
import InputComponent from "../../../inputs";
import ScrollBar from "../../../scrollBar";
import { CheckCircleGreenIcon, CloseCircleGrayIcon } from "../../../../assets/icons/svgIcons";

const fintocLogo = "https://dgdvd9d8e5bk8.cloudfront.net/fintoc.svg";

const defaultData = (company) => ({
  name: paymentsConstants.TYPES.Fintoc,
  factor: 1,
  duration: 60,
  startTime: "01:00",
  endTime: "23:59",
  details: [],
  useSandbox: false,
  _id: new mongoose.Types.ObjectId().toHexString(),
  IdCompany: company._id,
  subsidiaryId: "",
});

export default function FintocPaymentSettings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { recordChange } = useChangeRecord();

  // Selectores Redux
  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const subsidiaries = useSelector(selectSubsidiaries);
  const changes = useSelector(selectChangesDeposit);
  const isLoading = useSelector(selectLoadingUI);

  // ESTADO CLAVE: Diccionario para almacenar TODAS las sucursales al mismo tiempo
  // Estructura: { "subId": { apiKey: "", taxId: "", taxIdRaw: "", paymentId: "" } }
  const [credentialsMap, setCredentialsMap] = useState({});
  const [updated, setUpdated] = useState(false);
  const [useFintoc, setUseFintoc] = useState(false);
  const [payments, setPayments] = useState([]);
  const [subsidiaryId, setSubsidiaryId] = useState("");

  // Llave activa para saber qué datos mostrar/editar en el input
  const activeKey = company?.useSubsidiaryPayment && subsidiaryId ? subsidiaryId : "global";
  const currentData = credentialsMap[activeKey] || { apiKey: "", taxIdRaw: "", taxId: "" };


  const handleInputChange = (field, value) => {
    setCredentialsMap(prev => ({
      ...prev,
      [activeKey]: {
        ...(prev[activeKey] || { apiKey: "", taxIdRaw: "", taxId: "", paymentId: null }),
        [field]: value
      }
    }));
    setUpdated(true);
  };
  const handleSave = async () => {
    try {
      dispatch(loadingOn());

      const branchesToSave = company?.useSubsidiaryPayment ? subsidiaries.map(s => s._id) : ["global"];

      const savePromises = branchesToSave.map(async (subId) => {
        const data = credentialsMap[subId];

        // 1. Si el diccionario ni siquiera tiene la llave aún, saltar.
        if (!data) return null;

        // 2. OPTIMIZACIÓN: Si está todo vacío y NO existía previamente en la DB, saltar.
        if (!data.paymentId && !data.apiKey && !data.taxId) {
          return null;
        }

        // Promise.all para la encriptación
        let encryptedApiKey = "";
        if (data.apiKey) {
          encryptedApiKey = await lumaUtils.secretManagementUtils.encript(
            data.apiKey, config.secretKey, config.secretIv
          );
        }

        // Recuperar o inicializar el objeto payment original
        const originalPayment = payments.find(p =>
          subId === "global" ? !p.subsidiaryId : p.subsidiaryId === subId
        ) || {
          ...defaultData(company),
          subsidiaryId: subId === "global" ? "" : subId
        };

        const updatedPayment = {
          ...originalPayment,
          IdCompany: company._id,
          subsidiaryId: subId === "global" ? "" : subId,
          details: [
            {
              ...(originalPayment.details?.[0] || {}),
              apiKey: encryptedApiKey,
              taxId: data.taxId,
            },
          ],
        };

        // Retornar la promesa del endpoint
        return updatePayment(updatedPayment, user.token);
      });

      // Ejecutar TODAS las promesas de encriptación y endpoints en paralelo
      // Nota: el .filter(Boolean) es vital aquí, ya que elimina todos los 'null' 
      // que retornamos arriba, enviando a Promise.all SOLO las peticiones reales.
      await Promise.all(savePromises.filter(Boolean));

      await handleCompanySave();

      setUpdated(false);
      getPayments(company._id, user.token, dispatch);

      enqueueSnackbar(Notification({ text: "Métodos de pago actualizados correctamente", variant: "success", withDetails: false }));
    } catch (error) {
      console.error("Error saving Fintoc credentials:", error);
      enqueueSnackbar(Notification({ text: "Error al actualizar métodos de pago", variant: "error", withDetails: false }));
    } finally {
      dispatch(loadingOff());
    }
  };

  const handleCancel = () => {
    // Forzar una recarga llamando de nuevo a Redux para resetear el diccionario
    getPayments(company._id, user.token, dispatch);
    setUpdated(false);
  };

  const handleCompanySave = async () => {
    try {
      await companyService.updateCompany(
        company._id,
        { _id: company._id, UsePayments: { ...company.UsePayments, useFintoc } },
        user.token
      );
      await getCompany(company._id, dispatch);
      recordChange({ module: "Métodos de pago", submodule: "Fintoc", subsidiaryId: activeKey, changes });
    } catch (err) {
      console.error(err);
    }
  };

  const updateChanges = (fieldName, newValue) => {
    const newChanges = [...changes];
    const change = {
      text: "Usar método de pago Fintoc",
      oldValue: company.UsePayments[fieldName],
      newValue,
      date: dateUtils.getUTC().slice(0, 19).replace("T", " "),
    };

    const index = newChanges.findIndex((item) => item.text === change.text);
    if (index !== -1) {
      change.oldValue = newChanges[index].oldValue;
      newChanges[index] = change;
    } else {
      newChanges.push(change);
    }
    dispatch(setChanges(newChanges));
  };

  const handleDeleteCredential = async () => {
    const paymentIdToDelete = currentData.paymentId;
    if (!paymentIdToDelete) return;

    dispatch(loadingOn());
    try {
      const deleted = await paymentsService.deletePaymentCredentials(paymentIdToDelete, user?.token);
      if (deleted) {
        enqueueSnackbar(Notification({ text: "Se ha eliminado la credencial para la sucursal", variant: "success", withDetails: false }));
        // Limpiamos ese campo específico en el diccionario
        handleInputChange("apiKey", "");
        handleInputChange("taxIdRaw", "");
        handleInputChange("taxId", "");
        await getPayments(company._id, user.token, dispatch);
      }
    } catch (error) {
      console.error("Error deleting Fintoc credentials:", error);
      enqueueSnackbar(Notification({ text: "Error al tratar de eliminar credenciales", variant: "error", withDetails: false }));
    } finally {
      dispatch(loadingOff());
    }
  };




  // 1. Efectos de Inicialización Básica
  useEffect(() => {
    if (subsidiaries?.length > 0 && subsidiaries?.[0]?._id && company?.useSubsidiaryPayment) {
      setSubsidiaryId(subsidiaries?.[0]?._id);
    }
  }, [subsidiaries, company?.useSubsidiaryPayment]);

  useEffect(() => {
    setUseFintoc(company?.UsePayments?.useFintoc ? true : false);
    if (company?.Payments) {
      const found = company.Payments.filter(item => item.name === paymentsConstants.TYPES.Fintoc);
      setPayments(found);
    }
  }, [company?.Payments, company.UsePayments]);

  // 2. Desencriptación Masiva Inicial
  // Carga y desencripta los datos de TODAS las sucursales (o global) y los mete en el diccionario
  useEffect(() => {
    let isMounted = true;

    const loadAllCredentials = async () => {
      dispatch(loadingOn());
      const newMap = {};
      const branchesToProcess = company?.useSubsidiaryPayment ? subsidiaries : [{ _id: "global" }];

      await Promise.all(
        branchesToProcess.map(async (sub) => {
          const subId = sub._id;

          // Buscar payment en Redux
          const paymentData = payments.find(p =>
            subId === "global" ? !p.subsidiaryId : p.subsidiaryId === subId
          );

          const detail = paymentData?.details?.[0];
          let decryptedKey = "";

          if (detail?.apiKey && config?.secretKey && config?.secretIv) {
            try {
              decryptedKey = await lumaUtils.secretManagementUtils.deencript(
                detail.apiKey, config.secretKey, config.secretIv
              );
            } catch (error) {
              console.error("Error decrypting key for", subId, error);
            }
          }

          newMap[subId] = {
            apiKey: decryptedKey,
            taxId: detail?.taxId || "",
            taxIdRaw: detail?.taxId || "",
            paymentId: paymentData?._id || null
          };
        })
      );

      if (isMounted) {
        setCredentialsMap(newMap);
        setUpdated(false);
        dispatch(loadingOff());
      }
    };

    if (payments.length >= 0) {
      if (!company?.useSubsidiaryPayment || subsidiaries.length > 0) {
        loadAllCredentials();
      }
    }

    return () => { isMounted = false; };
  }, [payments, subsidiaries, company?.useSubsidiaryPayment]);
  // 4. Efecto Debounce para el RUT (aplica al input activo)
  useEffect(() => {
    const rawValue = credentialsMap[activeKey]?.taxIdRaw;
    if (!rawValue) return;

    const timerId = setTimeout(() => {
      try {
        const formatted = Rut.format(rawValue);
        setCredentialsMap(prev => ({
          ...prev,
          [activeKey]: {
            ...prev[activeKey],
            taxIdRaw: formatted,
            taxId: formatted
          }
        }));
      } catch {
        setCredentialsMap(prev => ({
          ...prev,
          [activeKey]: {
            ...prev[activeKey],
            taxId: rawValue
          }
        }));
      }
    }, 600);

    return () => clearTimeout(timerId);
  }, [credentialsMap[activeKey]?.taxIdRaw, activeKey]);


  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        {/* Botón regresar */}
        <button onClick={() => navigate(-1)} className={cx(styles.buttonBase, styles.backButton)}>
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <span>Regresar</span>
        </button>

        {/* Header Card */}
        <div className={styles.card}>
          <div className={styles.cardBody}>
            <div className={styles.headerFlex}>
              <div className={styles.headerInfo}>
                <div className={styles.logoBox}>
                  <img src={fintocLogo} alt="Fintoc" className={styles.logoImage} />
                </div>
                <div>
                  <h1 className={styles.mainTitle}>Fintoc</h1>
                  <p className={styles.subTitle}>
                    Permite a tus clientes pagar con transferencia bancaria directa desde cualquier banco de Chile
                  </p>
                </div>
              </div>

              {!useFintoc ? (
                <div className={styles.containerLogoInactive}>
                  <CloseCircleGrayIcon />
                  <span>Inactivo</span>
                </div>
              ) : (
                <div className={styles.containerLogoActive} style={{ fontWeight: 700 }}>
                  <CheckCircleGreenIcon />
                  <span>Activo</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cards de información */}
        <div className={styles.gridCards}>
          <div className={styles.card} style={{ marginBottom: 0 }}>
            <div className={styles.cardBody}>
              <div className={styles.infoCardFlex}>
                <div className={styles.iconBoxBlue}><AttachMoneyIcon /></div>
                <div>
                  <h3 className={styles.infoTitle}>Fintoc te ofrece pagos bancarios instantáneos</h3>
                  <p className={styles.infoText}>
                    <strong style={{ color: '#111827' }}>Pagas:</strong> 1% + IVA por transacción
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.card} style={{ marginBottom: 0 }}>
            <div className={styles.cardBody}>
              <div className={styles.infoCardFlex}>
                <div className={styles.iconBoxGreen}><FlashOnIcon /></div>
                <div>
                  <h3 className={styles.infoTitle}>Acreditación Inmediata</h3>
                  <p className={styles.infoText}>Recibe el dinero en tu cuenta al instante</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tarjeta de configuración */}
        <div className={styles.card}>
          <div className={styles.cardBody}>
            <div className={styles.formGroup} style={{ marginBottom: 0 }}>
              <div className={styles.switchBox}>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={useFintoc}
                    onChange={(e) => {
                      setUseFintoc(e.target.checked);
                      updateChanges("useFintoc", e.target.checked);
                      setUpdated(true);
                    }}
                  />
                  <span className={styles.slider}></span>
                </label>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: 14, fontWeight: 700, color: '#111827' }}>Activar integración Fintoc</p>
                  <p style={{ margin: 0, fontSize: 12, color: '#4b5563' }}>Habilitar Fintoc como método de pago en la tienda</p>
                </div>
              </div>
            </div>

            {company?.useSubsidiaryPayment && subsidiaries?.length > 0 && (
              <ScrollBar
                value={subsidiaryId}
                onChange={(newValue) => setSubsidiaryId(newValue)}
                list={subsidiaries}
                useCustomStyles={true}
                customStyles={{
                  wrapper: styles.wrapperclass,
                  tabList: styles.tabListClass,
                  tabItem: styles.tabItemClass,
                  activeTab: styles.activeTabClass,
                }}
              />
            )}

            {/* Formulario de configuración */}
            <div className={styles.formContainer}>
              {/* Secret Key */}
              <div className={styles.formGroup}>
                <label htmlFor="secret-key" className={styles.label}>
                  <VpnKeyIcon className={styles.labelIcon} />
                  Secret Key
                </label>
                <div className={styles.inputRow}>
                  <InputComponent
                    placeholder="sk_live_..."
                    type="text"
                    validation={updated && currentData.apiKey === ""}
                    helperText={updated && currentData.apiKey === "" ? "Ingresa tu Secret Key de Fintoc" : ""}
                    value={currentData.apiKey}
                    handler={(e) => handleInputChange("apiKey", e.target.value)}
                    key={`api-${activeKey}`}
                  />
                </div>
              </div>

              {/* RUT del comercio */}
              <div className={styles.formGroup}>
                <label htmlFor="rut" className={styles.label}>
                  <DescriptionIcon className={styles.labelIcon} />
                  RUT del comercio (Tax ID)
                </label>
                <InputComponent
                  placeholder="12.345.678-9"
                  type="text"
                  value={currentData.taxIdRaw}
                  handler={(e) => handleInputChange("taxIdRaw", e.target.value)}
                  key={`tax-${activeKey}`}
                />
              </div>

              <hr className={styles.separator} />

              {/* Eliminar credenciales */}
              <div className={styles.formGroup}>
                <div className={styles.dangerBox}>
                  <div>
                    <p className={styles.dangerTitle}>Eliminar credenciales</p>
                    <p className={styles.dangerText}>Esta acción borrará la key para la sucursal seleccionada</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleDeleteCredential}
                    disabled={isLoading || !currentData.paymentId}
                    className={styles.btnDanger}
                    style={{ opacity: (!currentData.paymentId || isLoading) ? 0.5 : 1 }}
                  >
                    <DeleteOutlineIcon style={{ fontSize: 18, marginRight: 8 }} />
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className={styles.actions}>
          <button
            type="button"
            onClick={handleCancel}
            disabled={!updated || isLoading}
            className={styles.btnCancel}
            style={{ opacity: (!updated || isLoading) ? 0.5 : 1 }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!updated || isLoading}
            className={styles.btnSave}
            style={{ opacity: (!updated || isLoading) ? 0.5 : 1 }}
          >
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}